// Chrome/layout state for the Data Assistant: whether it is open, and whether it
// renders as the right-hand drawer or as a full page.
//
// This is deliberately a SEPARATE provider from ChatResultsContext. That one holds
// the active result payload and re-renders every time a query returns; dock state is
// unrelated UI chrome and should not be coupled to it.
//
// The full-page mode works by portal rather than by mounting a second copy of
// ChatAssistant: AssistantPage registers its container element here, and the single
// ChatAssistant instance (mounted in Header) portals its body into that element. That
// keeps the conversation, the parsed rows and the review state alive when the user
// toggles between docked and full page - a second mount would reset all of it.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

export const ASSISTANT_ROUTE = '/commodity-management/assistant';

const DOCK_MODE_STORAGE_KEY = 'chatAssistantDockMode';

export type ChatDockMode = 'drawer' | 'fullPage';

interface ChatUiContextValue {
    /** Drawer visibility. Full-page visibility is governed by the route, not by this. */
    isOpen: boolean;
    mode: ChatDockMode;
    /** True when the user is currently on the full-page assistant route. */
    isFullPage: boolean;
    /** Element the full-page route wants the chat body portalled into, if mounted. */
    fullPageSlot: HTMLElement | null;
    openChat: () => void;
    closeChat: () => void;
    setMode: (mode: ChatDockMode) => void;
    registerFullPageSlot: (element: HTMLElement | null) => void;
}

const ChatUiContext = createContext<ChatUiContextValue | undefined>(undefined);

function readStoredMode(): ChatDockMode {
    try {
        return localStorage.getItem(DOCK_MODE_STORAGE_KEY) === 'fullPage' ? 'fullPage' : 'drawer';
    } catch (err) {
        // Private browsing / storage disabled - fall back to the docked default.
        console.warn('[ChatAssistant] Failed to read dock mode preference', err);
        return 'drawer';
    }
}

export function ChatUiProvider({ children }: { children: React.ReactNode }) {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setModeState] = useState<ChatDockMode>(readStoredMode);
    const [fullPageSlot, setFullPageSlot] = useState<HTMLElement | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const isFullPage = location.pathname === ASSISTANT_ROUTE;

    useEffect(() => {
        try {
            localStorage.setItem(DOCK_MODE_STORAGE_KEY, mode);
        } catch (err) {
            console.warn('[ChatAssistant] Failed to persist dock mode preference', err);
        }
    }, [mode]);

    const openChat = useCallback(() => {
        if (mode === 'fullPage') {
            if (!isFullPage) navigate(ASSISTANT_ROUTE);
            return;
        }
        setIsOpen(true);
    }, [mode, isFullPage, navigate]);

    // Going "back" from the full page is only safe when the user actually navigated
    // here from somewhere in the app. On a deep link or a refresh there is no in-app
    // history entry, and navigate(-1) would take them out of the SPA entirely - so
    // fall back to the dashboard instead. React Router records its position in
    // window.history.state.idx.
    const leaveFullPage = useCallback(() => {
        const historyIndex = (window.history.state as { idx?: number } | null)?.idx ?? 0;
        if (historyIndex > 0) {
            navigate(-1);
        } else {
            navigate('/');
        }
    }, [navigate]);

    const closeChat = useCallback(() => {
        setIsOpen(false);
        // Leaving the full-page view is a navigation, not just a state change.
        if (isFullPage) leaveFullPage();
    }, [isFullPage, leaveFullPage]);

    const setMode = useCallback((next: ChatDockMode) => {
        setModeState(next);
        if (next === 'fullPage') {
            setIsOpen(false);
            if (!isFullPage) navigate(ASSISTANT_ROUTE);
        } else {
            // Docking from the full page: come back to wherever the user was and
            // re-open the drawer so the conversation stays on screen.
            setIsOpen(true);
            if (isFullPage) leaveFullPage();
        }
    }, [isFullPage, navigate, leaveFullPage]);

    const registerFullPageSlot = useCallback((element: HTMLElement | null) => {
        setFullPageSlot(element);
    }, []);

    const value = useMemo<ChatUiContextValue>(
        () => ({ isOpen, mode, isFullPage, fullPageSlot, openChat, closeChat, setMode, registerFullPageSlot }),
        [isOpen, mode, isFullPage, fullPageSlot, openChat, closeChat, setMode, registerFullPageSlot]
    );

    return <ChatUiContext.Provider value={value}>{children}</ChatUiContext.Provider>;
}

export function useChatUi(): ChatUiContextValue {
    const ctx = useContext(ChatUiContext);
    if (!ctx) {
        throw new Error('useChatUi must be used within a ChatUiProvider');
    }
    return ctx;
}
